import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog'
import { faEdit, faPlus, faUsers, faWarning, faFire, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { MatTableDataSource } from '@angular/material/table';
import { AssessmentService } from 'src/app/services/assessment.service';
import { Observable, map, of, startWith } from 'rxjs';
import { FormControl } from '@angular/forms';
import { DialogService } from 'src/app/services/dialog.service';
import { Title } from '@angular/platform-browser';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { UserService } from 'src/app/services/user.service';
import { ReportService } from 'src/app/services/report.service';
import { ConfirmationToastComponent } from 'src/app/components/confirmation-toast/confirmation-toast.component';

@Component({
  selector: 'app-auth-status',
  templateUrl: './auth-status.component.html',
  styleUrls: ['./auth-status.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed,void', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: false
})


export class AuthStatusComponent implements OnInit {
  //icons
  faEdit = faEdit;
  faUsers = faUsers;
  faPlus = faPlus;
  faWarning = faWarning;
  faFire = faFire;
  faChevronRight = faChevronRight;

  //table variables
  pageNumber = 0;
  pageSize = 10;
  length = 0;
  sort = "updated_on"
  sortDirection = "desc"
  selectedForestFlag = false;
  assessments = new MatTableDataSource<any>();
  expandedAssessments: any;
  displayedColumns!: string[];

  //dropdown lists
  regions: any;
  yearsArray: any;

  //access
  @Input() user: any;

  //forest name filter
  forestNameList: string[];
  filtered_forest_names: Observable<string[]>;
  @ViewChild('forest') input?: ElementRef<HTMLInputElement>;
  forest_name = new FormControl("")
  selectedRegion: string | undefined = undefined;

  //search bar items
  filters = {
    region: null,
    forest_name: null as (string | null),
    baer_name: null,
    assessment_status_ids: [4, 11],
    fire_discovery_years: null,
  }
  filters_clean = true;

  constructor(
    public dialog: MatDialog,
    private readonly assessmentService: AssessmentService,
    private readonly dialogService: DialogService,
    private readonly titleService: Title,
    private readonly userService: UserService,
    private readonly reportsService: ReportService,
    public readonly toast: ConfirmationToastComponent,
  ) {
    this.titleService.setTitle("BAER - Assessments")
    this.regions = this.assessmentService.getRegionList();
    this.forestNameList = this.assessmentService.getForestNameList();
    this.filtered_forest_names = of(this.forestNameList)

    this.assessmentService.finishLoadingSubscription().subscribe(() => {
      this.regions = this.assessmentService.getRegionList();
      this.forestNameList = this.assessmentService.getForestNameList(this.filters.region as any);

      this.filtered_forest_names = of(this.forestNameList)
      this.filtered_forest_names = this.forest_name.valueChanges.pipe(
        startWith(""),
        map((value) => this._filter((value as string) || ""))
      )
    })

    const currentYear = new Date().getFullYear();
    const numberOfYears = 3; // You can change this to the desired number of years
    this.yearsArray = Array.from({ length: numberOfYears },
      (_, i) => currentYear - i);
    this.filters.fire_discovery_years = this.yearsArray[0];
    this.filters_clean = false;
  }

  regionChanged(): void {
    this.forestNameList = this.assessmentService.getForestNameList(this.filters.region as any);
    this.filtered_forest_names = this.forest_name.valueChanges.pipe(
      startWith(""),
      map((value) => this._filter((value as string) || ""))
    )
  }

  ngOnInit(): void {
    this.loadUser();

    this.displayedColumns = ['collapse',
      'baer_name',
      'region',
      'forest_name',
      'total_req',
      'total_auth',
      'actions'
    ]
    this.filtered_forest_names = this.forest_name.valueChanges.pipe(
      startWith(""),
      map((value) => this._filter((value as string) || ""))
    )
  }

  loadUser() {
    if (this.user.isWO) {
      this.loadAssessments();
      return;
    } else if ((this.user.isROCO?.length ?? 0) > 0) {
      this.filters.region = this.user.region;
      this.filters_clean = false;
      this.search();
      return;
    } else if ((this.user.isFOCO?.length ?? 0) > 0) {
      this.filters.region = this.user.region;
      this.forest_name.setValue(this.user.forest_name);
      this.filters_clean = false;
      this.search();
      return;
    } else if (this.user.teamLeadRoles) {
      this.user.isTeamLead = true;
      this.filters_clean = false;
      this.loadAssessments(true);
      return;
    } else {
      //default filter by forest
      this.filters.region = this.user.region;
      this.forest_name.setValue(this.user.forest_name);
      this.filters_clean = false;
    }
    this.search();

  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase()
    return this.forestNameList.filter((option: string) =>
      option.toLowerCase().includes(filterValue)
    )
  }
  filter(): void {
    const filterValue = this.input?.nativeElement.value.toLowerCase() ?? '';
    this.filtered_forest_names = of(this._filter(filterValue));
  }

  search(): void {
    this.filters_clean = false;
    this.pageNumber = 0;
    this.loadAssessments();
  }

  clearSearch(): void {
    this.filters_clean = true;
    this.pageNumber = 0;
    this.filters = {
      region: null,
      forest_name: null,
      baer_name: null,
      assessment_status_ids: [4, 11],
      fire_discovery_years: null,
    }
    this.forest_name.setValue("");
    this.regionChanged();
    this.loadAssessments();
  }

  pageEvent(e: any) {
    this.pageNumber = e.pageIndex;
    this.pageSize = e.pageSize;
    this.loadAssessments();
  }

  async loadAssessments(initialTeamLeadLoad?: boolean) {
    this.dialogService.openSpinner()
    this.filters.forest_name = this.forest_name.value ?? null;
    this.assessmentService.getAssessmentList(this.pageNumber + 1,
      this.pageSize, this.filters, this.sort, this.sortDirection, initialTeamLeadLoad).subscribe(
      {
        next: (data) => {
          this.assessments.data = data.assessments;
          this.length = data.totalResults;
          if (initialTeamLeadLoad) this.length = this.assessments.data.length;
          this.assessments.data.forEach((assessment: any) => {
            this.loadReportData(assessment);
          })
        },
        complete: () => {
          this.dialogService.closeSpinner();
          return true;
        },
        error: (err) => {
          return false;
        }
      });
  }

  loadReportData(assessment: any) {
    let reportRelations = '&relations=assessment,forests.treatments.treatment_specifications';
    this.reportsService.getReportList(assessment.assessment_id, reportRelations).subscribe(
      {
        next: (data) => {
          let reports = data.reports;
          let total_auth = 0;
          let total_req = 0;
          reports.sort((reportA: any, reportB: any) => reportA.report_num - reportB.report_num);
          reports.forEach((report: any) => {
            this.calcTotals(report);
            total_auth += report.total_auth;
            total_req += report.total_req;
          });
          assessment.reports = reports;
          assessment.total_auth = total_auth;
          assessment.total_req = total_req;
        }
      });
  }

  calcTotals(report: any) {
    let total_req = 0;
    let total_auth = 0;
    if (report.forests) {
      report.forests.forEach((forest: any) => {
        if (forest.treatments) {
          forest.treatments.forEach((treatment: any) => {
            let spec = treatment.treatment_specifications[0];
            if (spec) {
              total_req += ((spec.gis_unit_qty ?? spec.custom_unit_qty) ?? 0) * (spec.unit_cost ?? 0);
              total_auth += ((spec.approved_unit_qty ?? 0) *
                (spec.approved_unit_cost ?? 0)) + ((spec.wo_approved_unit_qty ?? 0)
                + (spec.wo_approved_unit_cost ?? 0));
            }
          });
        }
      });
    }
    report.total_req = total_req;
    report.total_auth = total_auth;
  }

  sortChange(sortState: any) {
    this.sort = sortState.active;
    this.sortDirection = sortState.direction;
    this.pageNumber = 0;
    this.loadAssessments()
  }

  clearValue(event: any) {
    event.target.value = ""
  }

  clearForest() {
    if (!this.selectedForestFlag) {
      this.forest_name.setValue("")
    }
    this.selectedForestFlag = false
  }

  forestSelected() {
    this.selectedForestFlag = true
  }

  stopPropagation(event: any) {
    event.stopPropagation();
  }
}
